import React, { useContext, useState, useEffect } from "react";
import { Link } from "gatsby";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import MenuIcon from "@mui/icons-material/Menu";
import Container from "@mui/material/Container";
import Drawer from "@mui/material/Drawer";
import AccountCircle from "@mui/icons-material/AccountCircle";
import LogoutIcon from "@mui/icons-material/Logout";
import MoreIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import { useTheme } from "@mui/material/styles";
import { Divider, ListItemIcon } from "@mui/material";
import TwitterIcon from "@mui/icons-material/Twitter";

import { SiteContext, logout, NavTree, ActionsContext } from "gatsby-frontend";

import { Search } from "./../../";

import { handleSubmenusNearEdge } from "../../utils/handleSubmenusNearEdge";

const drawerWidth = 320;

const ResponsiveAppBar = ({ main, tools }) => {
	const {
		privateRoute,
		authContext,
		titleAsText,
		title,
		subtitle,
		isPrivateLayout,
		twitterUsername,
		hasSearch,
		themeConfig,
	} = useContext(SiteContext);

	const { setEntity } = useContext(ActionsContext);

	const theme = useTheme();
	const mobileMenuId = "primary-search-account-menu-mobile";

	const [mobileOpen, setMobileOpen] = useState(false);
	const [openSearch, setOpenSearch] = useState(false);
	const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = useState(null);
	const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);

	const [homePath, setHomePath] = useState("/");
	const [logo, setLogo] = useState("/logo.png");
	const [appBarColors, setAppBarColors] = useState(theme.palette.appBar.public);

	const [userMenuAnchorEl, setUserMenuAnchorElAnchorEl] = useState(null);
	const openUserMenu = Boolean(userMenuAnchorEl);

	const navbarAlign = themeConfig?.customOptions?.navbarAlign || "left";

	useEffect(() => {
		setHomePath(isPrivateLayout ? privateRoute : "/");
		setLogo(isPrivateLayout ? "/logo-private.png" : "/logo.png");
		setAppBarColors(
			isPrivateLayout
				? theme.palette.appBar.private
				: theme.palette.appBar.public
		);
	}, [isPrivateLayout]);

	useEffect(() => {
		const observer = new MutationObserver((mutations) => {
			mutations.forEach((mutation) => {
				mutation.type === "childList" && handleSubmenusNearEdge();
			});
		});

		observer.observe(document.body, { childList: true, subtree: true });

		return () => {
			observer.disconnect();
		};
	}, []);

	const handleClickOpenSearch = () => {
		setOpenSearch(true);
	};

	const handleCloseSearch = () => {
		setOpenSearch(false);
	};

	const handleDrawerToggle = () => {
		setMobileOpen(!mobileOpen);
	};

	const handleMobileMenuOpen = (event) => {
		setMobileMoreAnchorEl(event.currentTarget);
	};

	const handleMobileMenuClose = () => {
		setMobileMoreAnchorEl(null);
	};

	const handleLogout = async (e) => {
		e.preventDefault();
		setEntity({});
		await logout(undefined, authContext);
	};

	const handleCloseUserMenu = () => {
		setUserMenuAnchorElAnchorEl(null);
	};

	const container =
		typeof window !== "undefined" ? () => window.document.body : undefined;

	const NavbarContainer = ({ children }) => {
		return (
			<Container
				{...(theme?.customOptions?.navbarFullWidth
					? { fluid: true }
					: { maxWidth: theme?.customOptions?.containerMaxWidth || "xl" })}
			>
				{children}
			</Container>
		);
	};

	const brand = (
		<Typography
			variant="h6"
			title="Go to start page"
			color="inherit"
			component={Link}
			className={`brand ${titleAsText ? "as-text" : "as-logo"}`}
			to={homePath}
			sx={(theme) => ({
				"& span": {
					display: "block",
					paddingTop: theme.spacing(1),
					paddingBottom: theme.spacing(1.2),
					height: 96,
					[theme.breakpoints.down("md")]: {
						height: 70,
						paddingTop: theme.spacing(1),
						paddingBottom: theme.spacing(1),
					},
					"& img": {
						height: "100%",
						width: "auto",
					},
				},
			})}
		>
			<span>
				{!titleAsText && <img src={logo} alt={titleAsText ? "logo" : title} />}
				{titleAsText && <strong>{title}</strong>}
				{titleAsText && subtitle && <em> {subtitle}</em>}
			</span>
		</Typography>
	);

	const renderMobileMenu = (
		<Menu
			anchorEl={mobileMoreAnchorEl}
			anchorOrigin={{
				vertical: "top",
				horizontal: "right",
			}}
			id={mobileMenuId}
			keepMounted
			transformOrigin={{
				vertical: "top",
				horizontal: "right",
			}}
			open={isMobileMenuOpen}
			onClose={handleMobileMenuClose}
		>
			{twitterUsername.length > 0 && (
				<MenuItem
					component="a"
					href={`https://twitter.com/${twitterUsername}`}
					target="_blank"
					rel="noopener"
					sx={{ "&::after": { display: "none !important" } }}
					className="twitter-menu-item"
				>
					<TwitterIcon />
					<span>Follow Us</span>
				</MenuItem>
			)}
			{hasSearch && (
				<MenuItem onClick={handleClickOpenSearch}>
					<SearchIcon />
					<span>Search</span>
				</MenuItem>
			)}
			{!!privateRoute && (
				<>
					<MenuItem component={Link} to={`${privateRoute}/profile`}>
						<AccountCircle />
						<span>Profile</span>
					</MenuItem>
					<Divider />
					<MenuItem
						onClick={(e) => {
							if (isPrivateLayout) handleLogout(e);
						}}
						component={isPrivateLayout ? "button" : Link}
						to={privateRoute}
					>
						{isPrivateLayout ? <LogoutIcon /> : <AccountCircle />}
						<span>{isPrivateLayout ? "Logout" : "Login"}</span>
					</MenuItem>
				</>
			)}
		</Menu>
	);

	const DesktopNavWrap = ({ children }) => {
		if (navbarAlign === "right") {
			return (
				<Box
					className="desktop-nav-wrap"
					sx={{ display: { xxs: "none", md: "flex" }, flexWrap: "wrap" }}
				>
					{children}
				</Box>
			);
		} else {
			return <>{children}</>;
		}
	};

	return (
		<>
			<AppBar
				position="static"
				color="transparent"
				sx={{
					backgroundColor: appBarColors.main,
					color: appBarColors.contrastText,
					...(appBarColors.gradient && {
						backgroundImage: appBarColors.gradient,
					}),
				}}
			>
				<NavbarContainer>
					<Toolbar
						disableGutters
						sx={(theme) => ({
							justifyContent: { xxs: "space-between" },
							color: appBarColors.contrastText,
						})}
					>
						<Box
							className="desktop-brand"
							sx={{ display: { xxs: "none", md: "flex" } }}
						>
							{brand}
						</Box>
						<Box
							sx={{ flexGrow: { md: 1 }, display: { xxs: "flex", md: "none" } }}
						>
							<IconButton
								size="large"
								aria-label="account of current user"
								aria-controls="menu-appbar"
								aria-haspopup="true"
								color="inherit"
								onClick={handleDrawerToggle}
							>
								<MenuIcon />
							</IconButton>
						</Box>
						<Box
							className="mobile-brand"
							sx={{ display: { xxs: "flex", md: "none" } }}
						>
							{brand}
						</Box>
						<DesktopNavWrap>
							{main && main.length > 0 && (
								<Box
									className="desktop-nav"
									sx={(theme) => {
										return {
											...(navbarAlign === "right" && {
												order: 2,
												width: "100%",
												justifyContent: "flex-end",
												marginBottom: theme.spacing(2.5),
											}),
											flexGrow: { md: 1 },
											display: { xxs: "none", md: "flex" },
											"& nav": {
												"& li.void-link": {
													"& > a": {
														cursor: "default",
													},
												},
												"& li.has-submenu": {
													position: "relative",
													"& > ul": {
														backgroundColor: theme.palette.grey["50"],
														borderRadius: theme.shape.borderRadius,
														boxShadow: theme.shadows[3],
														listStyle: "none",
														margin: 0,
														padding: 0,
														position: "absolute",
														zIndex: theme.zIndex.tooltip,
														top: "100%",
														left: theme.spacing(2),
														width: "280px",
														display: "none",
														...(navbarAlign === "right" && {
															left: "auto",
															right: theme.spacing(2),
														}),
														"& a": {
															fontWeight: theme.typography.fontWeightBold,
															color: theme.palette.grey["600"],
															padding: theme.spacing(1, 2.5),
															textDecoration: "none",
															display: "block",
															"&:hover, &:active, &:focus": {
																backgroundColor: "rgba(130,130,130,0.15)",
															},
														},
														"& > li": {
															"&:first-child": {
																"& > a": {
																	paddingTop: theme.spacing(2),
																	borderTopRightRadius:
																		theme.shape.borderRadius,
																	borderTopLeftRadius: theme.shape.borderRadius,
																},
															},
															"&:last-child": {
																"& > a": {
																	paddingBottom: theme.spacing(2),
																	borderBottomRightRadius:
																		theme.shape.borderRadius,
																	borderBottomLeftRadius:
																		theme.shape.borderRadius,
																},
															},
														},
													},
													"&.near-edge > ul": {
														left: "auto",
														right: "100%",
													},
													"&:hover": {
														"& > ul": {
															display: "block",
														},
													},
												},
												"& > ul": {
													display: "flex",
													listStyle: "none",
													margin: 0,
													padding: 0,
													marginLeft: theme.spacing(2),
													"& > li": {
														[theme.breakpoints.down("xl")]: {
															fontSize: theme.typography.fontSize,
														},
														"& > a": {
															textTransform: "uppercase",
															fontWeight: theme.typography.fontWeightBold,
															color: "inherit",
															padding: theme.spacing(0, 2),
															textDecoration: "none",
															[theme.breakpoints.down("xl")]: {
																padding: theme.spacing(0, 1.5),
															},
														},
														"&.has-submenu": {
															"& > ul": {
																"& ul": {
																	top: "0px",
																	left: "280px",
																},
															},
														},
													},
												},
											},
										};
									}}
								>
									<NavTree items={main} />
								</Box>
							)}

							<Box
								className="tools-nav"
								sx={{
									flexGrow: 0,
									display: { xxs: "none", md: "flex" },
									flexWrap: { md: "wrap" },
									justifyContent: { md: "right" },
									...(navbarAlign === "right" && {
										order: 1,
										width: "100%",
										justifyContent: "flex-end",
										alignItems: "center",
										paddingRight: theme.spacing(2),
										marginBottom: theme.spacing(1),
										paddingBottom: theme.spacing(1),
										paddingTop: theme.spacing(1.5),
									}),
								}}
							>
								{tools && tools.length > 0 && (
									<Box
										sx={(theme) => ({
											width: { md: "100%" },
											...(navbarAlign === "right" && {
												width: "auto",
												justifyContent: "flex-end",
											}),
											"& nav": {
												"& > ul": {
													display: "flex",
													listStyle: "none",
													justifyContent: "right",
													margin: 0,
													padding: 0,
													marginLeft: theme.spacing(2),
													"& > li": {
														// padding: theme.spacing(2, 0),
														"& > a": {
															textTransform: "uppercase",
															fontWeight: theme.typography.fontWeightBold,
															color: "inherit",
															padding: theme.spacing(0, 0, 0, 4),
															textDecoration: "none",
															...theme.typography.caption,
														},
													},
												},
											},
										})}
									>
										<NavTree items={tools} />
									</Box>
								)}
								<Box
									sx={{
										display: { xxs: "none", md: "flex" },
										...(navbarAlign === "right" && {
											marginRight: theme.spacing(-2),
											paddingLeft: theme.spacing(2),
											"& .MuiButtonBase-root": {
												padding: `5px !important`,
											},
										}),
									}}
								>
									{twitterUsername.length > 0 && (
										<IconButton
											size="large"
											aria-label="Search"
											color="inherit"
											component="a"
											href={`https://twitter.com/${twitterUsername}`}
											target="_blank"
											rel="noopener"
											sx={{ "&::after": { display: "none !important" } }}
											className="twitter-menu-item"
										>
											<TwitterIcon />
										</IconButton>
									)}
									{hasSearch && (
										<IconButton
											size="large"
											aria-label="Search"
											color="inherit"
											onClick={handleClickOpenSearch}
										>
											<SearchIcon />
										</IconButton>
									)}
									{!!privateRoute && (
										<>
											<IconButton
												size="large"
												edge="end"
												aria-label="account of current user"
												aria-haspopup="true"
												color="inherit"
												component={isPrivateLayout ? "button" : Link}
												to={privateRoute}
												onClick={(e) => {
													if (isPrivateLayout) {
														setUserMenuAnchorElAnchorEl(e.currentTarget);
													}
												}}
											>
												<AccountCircle />
											</IconButton>
											<Menu
												anchorEl={userMenuAnchorEl}
												id="user-menu"
												open={openUserMenu}
												onClose={handleCloseUserMenu}
												onClick={handleCloseUserMenu}
												PaperProps={{
													elevation: 0,
													sx: {
														overflow: "visible",
														filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
														mt: 1.5,
													},
												}}
												transformOrigin={{
													horizontal: "right",
													vertical: "top",
												}}
												anchorOrigin={{
													horizontal: "right",
													vertical: "bottom",
												}}
											>
												<MenuItem
													component={Link}
													to={`${privateRoute}/profile`}
												>
													<ListItemIcon>
														<AccountCircle fontSize="small" />
													</ListItemIcon>
													Profile
												</MenuItem>
												<Divider />
												<MenuItem onClick={handleLogout}>
													<ListItemIcon>
														<LogoutIcon fontSize="small" />
													</ListItemIcon>
													Logout
												</MenuItem>
											</Menu>
										</>
									)}
								</Box>
							</Box>
						</DesktopNavWrap>
						<Box sx={{ display: { xxs: "flex", md: "none" } }}>
							<IconButton
								size="large"
								aria-label="show more"
								aria-haspopup="true"
								color="inherit"
								aria-controls={mobileMenuId}
								onClick={handleMobileMenuOpen}
							>
								<MoreIcon />
							</IconButton>
						</Box>
					</Toolbar>
				</NavbarContainer>
			</AppBar>
			<Box component="nav">
				<Drawer
					container={container}
					variant="temporary"
					open={mobileOpen}
					onClose={handleDrawerToggle}
					ModalProps={{
						keepMounted: true, // Better open performance on mobile.
					}}
					sx={{
						display: { xxs: "block", md: "none" },
						"& .MuiDrawer-paper": {
							boxSizing: "border-box",
							width: drawerWidth,
						},
					}}
				>
					<Box
						onClick={handleDrawerToggle}
						sx={(theme) => ({
							padding: theme.spacing(2, 0),
							textAlign: "left",
							"& ul": {
								listStyle: "none",
								padding: 0,
								margin: 0,
								"& a": {
									display: "block",
									padding: theme.spacing(1, 3),
									textDecoration: "none",
									color: theme.palette.body.contrastText,
									"&:hover, &:active, &:focus": {
										backgroundColor: "rgba(130,130,130,0.15)",
									},
								},
							},
							"& nav": {
								"& > ul": {
									"& > li": {
										"& > a": {
											fontWeight: theme.typography.fontWeightBold,
										},
										"& > ul": {
											padding: theme.spacing(0, 0, 3),
											"& > li": {
												"& > a": {
													fontSize: theme.typography.fontSizeSm,
													padding: theme.spacing(1, 3, 1, 5),
												},
												"& > ul": {
													"& > li": {
														"& > a": {
															fontSize: "80%",
															padding: theme.spacing(0.5, 3, 0.5, 7),
															opacity: 0.6,
															"&:before": {
																display: "inline",
																content: '"- "',
															},
														},
													},
												},
											},
										},
									},
								},
							},
						})}
					>
						<NavTree items={main} />
						{tools && tools.length > 0 && (
							<>
								<Divider sx={(theme) => ({ margin: theme.spacing(2, 0) })} />
								<NavTree items={tools} />
							</>
						)}
					</Box>
				</Drawer>
			</Box>
			<Dialog
				open={openSearch}
				onClose={handleCloseSearch}
				fullWidth
				maxWidth="md"
				sx={{
					"& .MuiDialog-container": {
						alignItems: "flex-start",
					},
				}}
			>
				<DialogContent>
					<Search onSubmit={handleCloseSearch} autoFocus />
				</DialogContent>
			</Dialog>
			{renderMobileMenu}
		</>
	);
};
export default ResponsiveAppBar;
